import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ResearchProvider } from "@/contexts/ResearchContext";
import { AIPanelProvider } from "@/contexts/AIPanelContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import AuthGuard from "./components/AuthGuard";
import ShellRouter from "@/components/shell/ShellRouter";

// Public Pages
const Login = lazy(() => import("./pages/Login"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function useChunkReloadGuard() {
  useEffect(() => {
    const key = "chunk_reload";
    const shouldReload = (message: string) =>
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Loading chunk") ||
      message.includes("Loading CSS chunk");

    const handleWindowError = (event: ErrorEvent) => {
      if (!shouldReload(event.message ?? "")) {
        return;
      }

      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = String(event.reason?.message || event.reason || "");

      if (!shouldReload(message)) {
        return;
      }

      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    };

    sessionStorage.removeItem(key);
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);
}

function App() {
  useChunkReloadGuard();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AIPanelProvider>
            <ResearchProvider>
              <Suspense
                fallback={
                  <div className="flex h-screen items-center justify-center bg-slate-50">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-primary" />
                  </div>
                }
              >
                <Routes>
                  {/* Public */}
                  <Route path="/login" element={<Login />} />

                  {/* Workspace MDI (True Tabs) */}
                  <Route
                    path="*"
                    element={
                      <ErrorBoundary>
                        <AuthGuard>
                          <WorkspaceProvider>
                            <ShellRouter />
                          </WorkspaceProvider>
                        </AuthGuard>
                      </ErrorBoundary>
                    }
                  />
                </Routes>
              </Suspense>
            </ResearchProvider>
          </AIPanelProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
